import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import DateFilter from '../components/DateFilter';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const PIE_COLORS = ['#1a56db', '#f59e0b'];
const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v);

export default function FlightsPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async (params = {}, p = 1) => {
    setLoading(true);
    try {
      const res = await dashboardAPI.flights({ ...params, page: p, limit: 20 });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleFilter = (params) => { setFilters(params); setPage(1); load(params, 1); };
  const handlePage = (p) => { setPage(p); load(filters, p); };

  const filtered = data?.data?.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.yolcu_adi, r.bilet_no, r.cikis_parkur, r.varis_parkur, r.talep_eden].some(v => v && v.toLowerCase().includes(s));
  });

  if (loading && !data) return <div className="loading">Yükleniyor...</div>;

  return (
    <div>
      <div className="topbar">
        <h1>Uçak Bileti Analitik</h1>
        <DateFilter onFilter={handleFilter} />
      </div>

      {data && (
        <>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="label">Toplam Bilet</div>
              <div className="value">{data.total || 0}</div>
            </div>
            <div className="stat-card">
              <div className="label">Toplam Tutar</div>
              <div className="value">{fmt(data.totalAmount || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Farklı Yolcu</div>
              <div className="value">{data.uniquePassengers || 0}</div>
            </div>
            <div className="stat-card">
              <div className="label">Ort. Bilet Tutarı</div>
              <div className="value">{fmt(data.total ? (data.totalAmount || 0) / data.total : 0)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="chart-card">
              <h3>En Çok Kullanılan Rotalar</h3>
              <ResponsiveContainer width="100%" height={Math.max(220, (data.routes?.length || 5) * 30)}>
                <BarChart data={data.routes?.slice(0, 10)} layout="vertical" margin={{ left: 120, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="route" tick={{ fontSize: 10 }} width={115} />
                  <Tooltip formatter={(v, name) => [name === 'total' ? fmt(v) : v, name === 'total' ? 'Tutar' : 'Adet']} />
                  <Bar dataKey="count" name="Adet" fill="#1a56db" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Yurtiçi / Yurtdışı Dağılımı</h3>
              {data.hatTipi && data.hatTipi.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.hatTipi}
                        dataKey="count"
                        nameKey="hat_tipi"
                        cx="50%" cy="50%"
                        outerRadius={85}
                        label={({ hat_tipi, percent }) => `${hat_tipi} %${(percent * 100).toFixed(0)}`}
                        labelLine={{ strokeWidth: 1 }}
                      >
                        {data.hatTipi.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, name, props) => [v + ' bilet', props.payload.hat_tipi]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 13 }}>
                    {data.hatTipi.map((h, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: PIE_COLORS[i] }}>{h.hat_tipi}</div>
                        <div>{h.count} bilet</div>
                        <div style={{ fontWeight: 600 }}>{fmt(h.total)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Veri yok</div>}
            </div>
          </div>

          <div className="chart-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3>Bilet Detayları ({data.total} kayıt)</h3>
              <input type="text" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 220 }} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table className="data-table">
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <tr>
                    <th>Yolcu</th>
                    <th>Bilet No</th>
                    <th>Hat Tipi</th>
                    <th>Rota</th>
                    <th>Tutar</th>
                    <th>Talep Eden</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map(r => (
                    <tr key={r.id}>
                      <td>{r.yolcu_adi}</td>
                      <td>{r.bilet_no}</td>
                      <td>{r.hat_tipi}</td>
                      <td>{r.cikis_parkur} → {r.varis_parkur}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(r.satis_fatura_bedeli)}</td>
                      <td>{r.talep_eden}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => handlePage(page - 1)}>← Önceki</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{page} / {data.totalPages}</span>
                <button disabled={page >= data.totalPages} onClick={() => handlePage(page + 1)}>Sonraki →</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
